// Globals
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

// UI
import { message, Modal } from 'antd';
import AvatarEditor from 'react-avatar-editor';
import ImageEditor from '../ImageEditor/ImageEditor';

// Locals
import {
  defaultImageEditorProps,
  getBlobFromAvatarImage,
  getPathForUserImage,
  getSignedUrl,
  UPLOAD_BUCKET,
  uploadPostFile,
} from '../utils';
import styles from './Account.module.css';
import useUser from '../../hooks/useUser';
import {
  clearEditorData,
  setAvatar,
  setImageMimeTypeLoaded,
  updateUser as updateUserAction,
} from '../../actions';

// Types
import { ImageEditorProps } from '../../types';
import { IState } from '../../graphql/types';
import { API, graphqlOperation } from 'aws-amplify';
import { updateUserProfile } from '../../graphql/mutations';
import { UpdateUserProfileMutation } from '../../graphql/api';
import { createImageUploadRecord } from '../../hooks/useFeedCardForm';

interface IProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}

const AccountUploadPhotoModal: React.FC<IProps> = ({
  showModal,
  setShowModal,
}) => {
  const [editorRef, setEditorRef] = useState<AvatarEditor | null>(null);

  const { user } = useUser();

  const dispatch = useDispatch();

  const avatarUrl = useSelector<IState, string | undefined>(
    state => state.avatar
  );

  const [formAvatarUrl, setFormAvatarUrl] = useState<string | null>(
    avatarUrl ?? null
  );

  const [saving, setSaving] = useState<boolean>(false);

  const [profileImageName, setProfileImageName] = useState<string | null>(
    user?.profileImageName ?? null
  );

  const uploadFile = useCallback(
    async (file, fileName) => {
      const key = getPathForUserImage(user?.tenantID!, user?.userId!);

      const data = await getSignedUrl(
        `${key}/account/${fileName}`,
        UPLOAD_BUCKET,
        'asset',
        'post'
      );

      const { url, fields } = data;

      if (fields && url) {
        const uploadedImg = await uploadPostFile(url, file, fields);
        if (uploadedImg) {
          const imageRecordParams = {
            userID: user?.userId,
            id: fileName,
            fileName: profileImageName,
            imageUrl: `studio/${user?.tenantID}/${user?.userId}/avatar/${fileName}`,
            imageService: 'cloudinary',
            createdAt: String(Date.now()),
            state: 'uploading',
            type: 'avatar',
          };

          await createImageUploadRecord(imageRecordParams);

          return true;
        }
      }

      return false;
    },
    [user, profileImageName]
  );

  const handleRemove = useCallback(() => {
    setFormAvatarUrl('');
    setProfileImageName('');
    dispatch(clearEditorData());
  }, [dispatch]);

  const handleLoadImage = useCallback(
    file => {
      dispatch(setImageMimeTypeLoaded(file.type.toLowerCase()));
      setFormAvatarUrl(URL.createObjectURL(file));
      setProfileImageName(file.name);
    },
    [dispatch]
  );

  const handleClose = useCallback(() => {
    setShowModal(false);
    dispatch(clearEditorData());
    setFormAvatarUrl(null);
  }, [dispatch, setShowModal]);

  const updateProfileImageName = useCallback(
    async fileName => {
      const result = await (API.graphql(
        graphqlOperation(updateUserProfile, {
          input: {
            profileImageName,
            profileImageId: fileName,
            profileImageService: 'cloudinary',
          },
        })
      ) as Promise<{ data: UpdateUserProfileMutation }>);

      if (result.data.updateUserProfile) {
        dispatch(
          updateUserAction({
            profileImageName:
              result.data.updateUserProfile.profileImageName || '',
          })
        );
      }
    },
    [dispatch, profileImageName]
  );

  const handleSave = useCallback(async () => {
    if (editorRef) {
      setSaving(true);

      try {
        const img = await getBlobFromAvatarImage(editorRef);

        if (img) {
          const imageFileName = uuid();
          const uploadedFile = await uploadFile(img, imageFileName);

          if (uploadedFile) {
            dispatch(setAvatar(URL.createObjectURL(img)));
            await updateProfileImageName(imageFileName);
          }
        }
      } catch (error) {
        console.log(error);
        if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unkown error occurred.');
        }
      }
    }

    setSaving(false);
    handleClose();
  }, [editorRef, dispatch, handleClose, uploadFile, updateProfileImageName]);

  React.useEffect(() => {
    if (formAvatarUrl === null) {
      setFormAvatarUrl(avatarUrl ?? null);
    }
    if (profileImageName === null) {
      setProfileImageName(user?.profileImageName ?? null);
    }
  }, [profileImageName, formAvatarUrl, avatarUrl, user]);

  const imageEditorProps = useMemo<ImageEditorProps>(
    () => ({
      ...defaultImageEditorProps,
      image: formAvatarUrl,
      fileName: profileImageName || 'unnamed_file',
      handleRemove,
      saving,
      handleLoadImage,
      editorRef,
      setEditorRef,
      disabled: saving,
      loading: !formAvatarUrl && !!profileImageName,
      imgWidth: 230,
      imgHeight: 230,
      imgBorderWidth: 145.5,
      imgBorderHeight: 95.5,
      sliderWidth: 200.75,
      imgBorderRadius: 115,
      removeTextClass: styles.removeImageText,
      boxSizeClass: styles.imgEditorBoxSize,
      staticImageClass: styles.staticImage,
    }),
    [
      saving,
      handleLoadImage,
      formAvatarUrl,
      handleRemove,
      editorRef,
      setEditorRef,
      profileImageName,
    ]
  );

  return (
    <>
      <Modal
        title={<div className={'text-center'}>Change Profile Picture</div>}
        open={showModal}
        onOk={handleSave}
        okButtonProps={{ disabled: saving, loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        centered
        onCancel={handleClose}
        width={600}
      >
        <div style={{ paddingBottom: '24px', marginTop: '16px' }}>
          <ImageEditor props={imageEditorProps} />
        </div>
      </Modal>
    </>
  );
};

export default AccountUploadPhotoModal;
